import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, type AudioAsset, type AudioAssetKind } from "../../api";

/**
 * Geteilte Audio-Library-Komponente. Lädt einmalig die Liste der hochgeladenen
 * Audiodateien vom Server, zeigt Upload-Zone + Asset-Liste mit Preview/Play,
 * unterstützt Drag&Drop auf die Timeline und einen "Verwenden"-Button für
 * den Music-Tab.
 *
 * Die Liste wird global gepflegt — einmal hochgeladen, in jedem Reel verfügbar.
 *
 * - `kindFilter` schränkt die Liste auf einen Typ ein (music | sfx | all).
 * - `dragMime` setzt den Drag-MIME, sodass die Timeline weiß, was passiert.
 * - `onSelect`  wird beim "Verwenden"-Klick ausgelöst (nicht via D&D).
 */
export const AUDIO_DRAG_MIME = "application/x-allyver-audio";

type Props = {
  /** Filtert die angezeigten Assets. "all" = alle Kinds. */
  kindFilter: "music" | "sfx" | "voiceover" | "all";
  /** Default-Kind für neu hochgeladene Dateien. */
  defaultKind: AudioAssetKind;
  /** Klick auf Karte / "Verwenden"-Button. */
  onSelect?: (asset: AudioAsset) => void;
  /** Optional: erlaubt der Karte einen "Drag-out" auf die Timeline.
   *  Wenn `null`, wird Drag deaktiviert. */
  dragMime?: string | null;
  /** Kompakte Variante (für eingebettete Verwendung im MusicPanel). */
  compact?: boolean;
  /** Externe Refresh-Anforderung — z. B. nach Drag/Drop-Upload. */
  refreshKey?: number;
};

const fmtBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDur = (sec: number): string => {
  if (!sec || !Number.isFinite(sec)) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const AudioLibrary: React.FC<Props> = ({
  kindFilter,
  defaultKind,
  onSelect,
  dragMime = AUDIO_DRAG_MIME,
  compact = false,
  refreshKey = 0,
}) => {
  const [items, setItems] = useState<AudioAsset[]>([]);
  const [filter, setFilter] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.audioList();
      setItems(r.items);
    } catch (e) {
      setErr(`Konnte Audio-Library nicht laden: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // Probe duration the first time we see an asset whose durationSec === 0.
  // We do this once on the client because the server doesn't ship ffprobe.
  useEffect(() => {
    const pending = items.filter((a) => !a.durationSec || a.durationSec <= 0);
    if (pending.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const asset of pending) {
        try {
          const dur = await probeDuration(asset.url);
          if (cancelled) return;
          if (dur > 0) {
            await api.audioPatch(asset.id, { durationSec: dur });
            setItems((prev) =>
              prev.map((a) => (a.id === asset.id ? { ...a, durationSec: dur } : a)),
            );
          }
        } catch {
          /* swallow — leave duration at 0 */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = items.filter((a) => {
      if (kindFilter !== "all" && a.kind !== kindFilter) return false;
      if (favOnly && !a.favorite) return false;
      if (!q) return true;
      return (
        a.displayName.toLowerCase().includes(q) ||
        a.filename.toLowerCase().includes(q) ||
        (a.tags ?? []).some((t) => t.toLowerCase().includes(q))
      );
    });
    // Favoriten zuerst, sonst nach Upload-Datum (Server liefert bereits sortiert).
    return [...list].sort((a, b) => Number(!!b.favorite) - Number(!!a.favorite));
  }, [items, filter, kindFilter, favOnly]);

  const handleToggleFavorite = useCallback(async (asset: AudioAsset): Promise<void> => {
    const next = !asset.favorite;
    setItems((prev) => prev.map((a) => (a.id === asset.id ? { ...a, favorite: next } : a)));
    try {
      await api.audioPatch(asset.id, { favorite: next });
    } catch {
      // Revert bei Fehler.
      setItems((prev) => prev.map((a) => (a.id === asset.id ? { ...a, favorite: !next } : a)));
    }
  }, []);

  const favCount = useMemo(
    () => items.filter((a) => a.favorite && (kindFilter === "all" || a.kind === kindFilter)).length,
    [items, kindFilter],
  );

  const playPreview = useCallback((asset: AudioAsset) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (previewing === asset.id) {
      a.pause();
      setPreviewing(null);
      return;
    }
    a.pause();
    a.src = asset.url;
    a.currentTime = 0;
    a.volume = 0.7;
    setPreviewing(asset.id);
    a.play().catch(() => setPreviewing(null));
    a.onended = () => setPreviewing((p) => (p === asset.id ? null : p));
  }, [previewing]);

  useEffect(() => () => audioRef.current?.pause(), []);

  const handleFiles = useCallback(
    async (files: FileList | File[] | null): Promise<void> => {
      if (!files || files.length === 0) return;
      setUploading(true);
      setErr(null);
      try {
        await api.audioUpload(files, defaultKind);
        await load();
      } catch (e) {
        setErr(`Upload fehlgeschlagen: ${(e as Error).message}`);
      } finally {
        setUploading(false);
      }
    },
    [defaultKind, load],
  );

  const handleDelete = useCallback(
    async (asset: AudioAsset): Promise<void> => {
      if (!window.confirm(`„${asset.displayName}" endgültig aus der Library entfernen?`)) return;
      try {
        await api.audioDelete(asset.id);
        setItems((prev) => prev.filter((a) => a.id !== asset.id));
      } catch (e) {
        setErr(`Löschen fehlgeschlagen: ${(e as Error).message}`);
      }
    },
    [],
  );

  const handleRename = useCallback(
    async (asset: AudioAsset, name: string): Promise<void> => {
      const next = name.trim();
      if (!next || next === asset.displayName) {
        setEditingId(null);
        return;
      }
      try {
        const updated = await api.audioPatch(asset.id, { displayName: next });
        setItems((prev) => prev.map((a) => (a.id === asset.id ? updated : a)));
      } catch (e) {
        setErr(`Umbenennen fehlgeschlagen: ${(e as Error).message}`);
      } finally {
        setEditingId(null);
      }
    },
    [],
  );

  const handleKindToggle = useCallback(
    async (asset: AudioAsset): Promise<void> => {
      const nextKind: AudioAssetKind =
        asset.kind === "music"
          ? "sfx"
          : asset.kind === "sfx"
            ? "voiceover"
            : "music";
      try {
        const updated = await api.audioPatch(asset.id, { kind: nextKind });
        setItems((prev) => prev.map((a) => (a.id === asset.id ? updated : a)));
      } catch (e) {
        setErr(`Kategorie ändern fehlgeschlagen: ${(e as Error).message}`);
      }
    },
    [],
  );

  // Drag-Zone für die ganze Komponente: Datei in den Bereich ziehen → Upload.
  const [isDragOver, setIsDragOver] = useState(false);
  const onZoneDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes("Files")) {
      e.preventDefault();
      setIsDragOver(true);
    }
  };
  const onZoneDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  };
  const onZoneDrop = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("audio/") ||
      /\.(mp3|wav|m4a|aac|ogg|oga|flac|webm|mp4)$/i.test(f.name),
    );
    if (files.length === 0) {
      setErr("Bitte nur Audio-Dateien ablegen.");
      return;
    }
    void handleFiles(files);
  };

  return (
    <div
      className={`audio-library ${compact ? "compact" : ""} ${isDragOver ? "drag-over" : ""}`}
      onDragOver={onZoneDragOver}
      onDragLeave={onZoneDragLeave}
      onDrop={onZoneDrop}
    >
      <div className="audio-library-head">
        <button
          type="button"
          className="btn primary sm audio-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Audio-Datei vom Computer auswählen (mp3, wav, m4a, ogg, …)"
        >
          {uploading ? "Lädt…" : "+ Audio hochladen"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.oga,.flac,.webm"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {!compact && (
          <input
            type="search"
            className="audio-library-search"
            placeholder="Suchen…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        )}
        <button
          type="button"
          className={`audio-fav-filter ${favOnly ? "on" : ""}`}
          onClick={() => setFavOnly((v) => !v)}
          title={favOnly ? "Alle anzeigen" : "Nur Favoriten anzeigen"}
          aria-pressed={favOnly}
        >
          {favOnly ? "★" : "☆"} {favCount > 0 ? favCount : ""}
        </button>
      </div>

      {err && <div className="audio-library-error">{err}</div>}

      <div className="audio-library-list">
        {loading && items.length === 0 ? (
          <div className="audio-library-empty">Lädt…</div>
        ) : filtered.length === 0 ? (
          <div className="audio-library-empty">
            {items.length === 0
              ? "Noch keine eigenen Audios. Datei hochladen oder hier reinziehen."
              : "Keine Treffer."}
          </div>
        ) : (
          filtered.map((asset) => {
            const isPlaying = previewing === asset.id;
            const isEditing = editingId === asset.id;
            return (
              <div
                key={asset.id}
                className={`audio-card ${isPlaying ? "playing" : ""}`}
                draggable={!!dragMime}
                onDragStart={(e) => {
                  if (!dragMime) return;
                  e.dataTransfer.setData(
                    dragMime,
                    JSON.stringify({
                      assetId: asset.id,
                      url: asset.url,
                      displayName: asset.displayName,
                      durationSec: asset.durationSec,
                      kind: asset.kind,
                    }),
                  );
                  // Backwards-compat: also set the SFX-Drag mime when this is
                  // an SFX card, so the existing SFX-lane drop handler picks
                  // it up without further changes.
                  if (asset.kind === "sfx") {
                    e.dataTransfer.setData(
                      "application/x-allyver-sfx",
                      JSON.stringify({
                        sound: "click",
                        defaultDurSec: asset.durationSec || 1.0,
                        custom: { assetId: asset.id, url: asset.url, displayName: asset.displayName },
                      }),
                    );
                  }
                  e.dataTransfer.effectAllowed = "copy";
                }}
              >
                <button
                  type="button"
                  className="audio-card-play"
                  onClick={() => playPreview(asset)}
                  title={isPlaying ? "Stop" : "Vorschau abspielen"}
                  aria-label={isPlaying ? "Stop" : "Play"}
                >
                  {isPlaying ? "■" : "▶"}
                </button>

                <div className="audio-card-meta">
                  {isEditing ? (
                    <input
                      autoFocus
                      className="audio-card-rename"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => void handleRename(asset, editName)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleRename(asset, editName);
                        else if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="audio-card-name"
                      onClick={() => {
                        setEditingId(asset.id);
                        setEditName(asset.displayName);
                      }}
                      title="Klick zum Umbenennen"
                    >
                      {asset.displayName}
                    </button>
                  )}
                  <div className="audio-card-sub">
                    <span
                      className={`audio-card-kind kind-${asset.kind}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleKindToggle(asset);
                      }}
                      title="Kategorie wechseln (Music ⇄ SFX ⇄ Voiceover)"
                    >
                      {asset.kind === "music"
                        ? "🎵 Music"
                        : asset.kind === "voiceover"
                          ? "🎙️ VO"
                          : "🔊 SFX"}
                    </span>
                    <span className="audio-card-dot">·</span>
                    <span>{fmtDur(asset.durationSec)}</span>
                    <span className="audio-card-dot">·</span>
                    <span>{fmtBytes(asset.size)}</span>
                  </div>
                </div>

                <div className="audio-card-actions">
                  <button
                    type="button"
                    className={`audio-card-fav ${asset.favorite ? "on" : ""}`}
                    onClick={() => void handleToggleFavorite(asset)}
                    title={asset.favorite ? "Favorit entfernen" : "Als Favorit markieren"}
                    aria-pressed={!!asset.favorite}
                  >
                    {asset.favorite ? "★" : "☆"}
                  </button>
                  {onSelect && (
                    <button
                      type="button"
                      className="btn ghost xs"
                      onClick={() => onSelect(asset)}
                      title="Im Reel verwenden"
                    >
                      Verwenden
                    </button>
                  )}
                  <button
                    type="button"
                    className="icon-btn xs danger"
                    onClick={() => void handleDelete(asset)}
                    title="Aus Library entfernen"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      {isDragOver && <div className="audio-library-drop-hint">Hier loslassen — Audio-Datei hochladen</div>}
    </div>
  );
};

/** HTMLAudioElement-basierter Probe für die Quell-Dauer in Sekunden. */
function probeDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    const a = new Audio();
    a.preload = "metadata";
    const finish = (sec: number) => {
      a.src = "";
      resolve(Number.isFinite(sec) && sec > 0 ? sec : 0);
    };
    a.addEventListener("loadedmetadata", () => finish(a.duration), { once: true });
    a.addEventListener("error", () => finish(0), { once: true });
    setTimeout(() => finish(a.duration || 0), 4000);
    a.src = url;
  });
}
